import type { FollowUpSheetResponse } from '@lms/shared'
import { Check, ChevronsUpDown, UserCog } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAssignSupervisor, useClearSupervisorAssignment } from '@/hooks/use-follow-ups'
import { useOrganizationMembers } from '@/hooks/use-organizations'
import { cn } from '@/lib/utils'

interface SupervisorAssignmentCardProps {
  enrollmentId: string
  supervisor: FollowUpSheetResponse['supervisor']
}

/** 人事向け: 受講者ごとの上司評価者の個別指定 */
export function SupervisorAssignmentCard({
  enrollmentId,
  supervisor,
}: SupervisorAssignmentCardProps) {
  const { data: members = [] } = useOrganizationMembers()
  const assignSupervisor = useAssignSupervisor()
  const clearAssignment = useClearSupervisorAssignment()
  const [open, setOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const selected = members.find((member) => member.userId === selectedUserId)

  const handleAssign = async () => {
    if (!selectedUserId) return
    try {
      await assignSupervisor.mutateAsync({ enrollmentId, data: { userId: selectedUserId } })
      toast.success('上司評価者を指定しました')
      setSelectedUserId(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '指定に失敗しました')
    }
  }

  const handleClear = async () => {
    if (
      !confirm('個別指定を解除し、組織マスター上の上司が評価するように戻します。よろしいですか？')
    ) {
      return
    }
    try {
      await clearAssignment.mutateAsync(enrollmentId)
      toast.success('個別指定を解除しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '解除に失敗しました')
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCog className="h-4 w-4" />
          上司評価者の個別指定（人事）
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          原則は評価時点の組織マスター上の上司が評価します。異動直後で評価できない場合などは、この受講者の上司評価者を指定できます（提出済みの評価は変わりません）。
        </p>
        <p className="text-sm">
          現在の上司評価者:{' '}
          {supervisor
            ? `${supervisor.name}（${supervisor.isAssigned ? '個別指定' : '組織マスター'}）`
            : '未設定（組織マスターに上司が登録されていません）'}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-72 justify-between"
              >
                <span className="truncate">{selected ? selected.name : '上司評価者を選択'}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0">
              <Command>
                <CommandInput placeholder="氏名・社員番号・部署で検索" />
                <CommandList>
                  <CommandEmpty>該当するメンバーがいません</CommandEmpty>
                  <CommandGroup>
                    {members.map((member) => (
                      <CommandItem
                        key={member.userId}
                        value={`${member.name} ${member.employeeNumber ?? ''} ${member.departmentName ?? ''}`}
                        onSelect={() => {
                          setSelectedUserId(member.userId)
                          setOpen(false)
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            member.userId === selectedUserId ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="truncate">{member.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {member.departmentName}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            onClick={handleAssign}
            disabled={!selectedUserId || assignSupervisor.isPending}
          >
            指定する
          </Button>
          {supervisor?.isAssigned && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={clearAssignment.isPending}
            >
              組織マスターの上司に戻す
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
